import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";

function GetUser() {
  const [users, setUsers] = useState([]);
  const [userCount, setUserCount] = useState("Loading...");
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [showUserDetails, setShowUserDetails] = useState({});
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedPincode, setSelectedPincode] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [pincodes, setPincodes] = useState([]);
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [isServiceOpen, setIsServiceOpen] = useState(false);
  const [searchService, setSearchService] = useState("");
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [searchShop, setSearchShop] = useState("");
  const [isCityOpen, setIsCityOpen] = useState(false);
  const [searchCity, setSearchCity] = useState("");
  const [isPincodeOpen, setIsPincodeOpen] = useState(false);
  const [searchPincode, setSearchPincode] = useState("");
  const serviceDropdownRef = useRef(null);
  const shopDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);
  const pincodeDropdownRef = useRef(null);

  const limit = 20;

  // Filter services, shops, cities, and pincodes based on search terms
  const filteredServices = services
    .map((s) => ({
      value: s._id,
      label: s.serviceName || "N/A",
    }))
    .filter((service) =>
      service.label.toLowerCase().includes(searchService.toLowerCase())
    );

  const filteredShops = shops
    .map((shop) => ({
      value: shop,
      label: shop || "N/A",
    }))
    .filter((shop) =>
      shop.label.toLowerCase().includes(searchShop.toLowerCase())
    );

  const filteredCities = cities
    .map((city) => ({
      value: city,
      label: city,
    }))
    .filter((city) =>
      city.label.toLowerCase().includes(searchCity.toLowerCase())
    );

  const filteredPincodes = pincodes
    .map((pincode) => ({
      value: pincode,
      label: pincode,
    }))
    .filter((pincode) =>
      pincode.label.toLowerCase().includes(searchPincode.toLowerCase())
    );

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const response = await axios.get(
          "https://visitguru.in/api/v1/user/getalluser",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("User API Response:", response.data);
        if (response.data.success && Array.isArray(response.data.data)) {
          const enhancedUsers = response.data.data.map((user) => {
            const totalOrderAmount =
              user.bookings?.reduce((sum, booking) => {
                return (
                  sum +
                  (booking.services?.reduce(
                    (subSum, service) => subSum + (service.subtotal || 0),
                    0
                  ) || 0)
                );
              }, 0) || 0;
            return { ...user, totalOrderAmount };
          });

          const uniqueUsers = Array.from(
            new Map(enhancedUsers.map((item) => [item._id, item])).values()
          );
          const sorted = uniqueUsers.sort((a, b) => {
            const aDate = a.bookings?.[0]?.createdAt
              ? new Date(a.bookings[0].createdAt)
              : new Date(0);
            const bDate = b.bookings?.[0]?.createdAt
              ? new Date(b.bookings[0].createdAt)
              : new Date(0);
            return bDate - aDate;
          });
          setUsers(sorted);
          setUserCount(sorted.length);

          const uniqueCitiesObj = {};
          sorted.forEach((u) => {
            if (u.city) {
              const city = u.city.trim();
              const key = city.toLowerCase();
              if (!uniqueCitiesObj[key]) {
                uniqueCitiesObj[key] =
                  city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
              }
            }
          });
          setCities(Object.values(uniqueCitiesObj));

          const uniquePincodes = [
            ...new Set(sorted.map((u) => u.pincode).filter(Boolean)),
          ].sort((a, b) => a.localeCompare(b));
          setPincodes(uniquePincodes);

          const uniqueShops = [
            ...new Set(
              sorted.flatMap((u) =>
                u.bookings?.map((b) => b.shopName).filter(Boolean)
              )
            ),
          ].sort((a, b) => a.localeCompare(b));
          setShops(uniqueShops);
        } else {
          setUserCount(0);
          setUsers([]);
          toast.error("No user data found");
        }
      } catch (err) {
        console.error(
          "Error fetching users:",
          err.response?.data || err.message
        );
        setUserCount(0);
        setUsers([]);
        toast.error("Failed to load users: " + err.message);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const response = await axios.get(
          "https://visitguru.in/api/v1/category/getCategory",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.success) {
          setCategories(response.data.data || []);
        } else {
          toast.error("No categories found");
        }
      } catch (err) {
        console.error(
          "Error fetching categories:",
          err.response?.data || err.message
        );
        toast.error("Failed to load categories: " + err.message);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const url = selectedCategory
          ? `https://visitguru.in/api/v1/service/getService?categoryName=${encodeURIComponent(
            selectedCategory
          )}`
          : "https://visitguru.in/api/v1/service/getService";

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data.success) {
          const allServices = response.data.data || [];
          setServices(allServices);
          setSelectedService("");
        } else {
          setServices([]);
          toast.error("No services found");
        }
      } catch (err) {
        console.error(
          "Error fetching services:",
          err.response?.data || err.message
        );
        setServices([]);
        toast.error("Failed to load services: " + err.message);
      }
    };

    fetchServices();
  }, [selectedCategory]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        serviceDropdownRef.current &&
        !serviceDropdownRef.current.contains(event.target)
      ) {
        setIsServiceOpen(false);
      }
      if (
        shopDropdownRef.current &&
        !shopDropdownRef.current.contains(event.target)
      ) {
        setIsShopOpen(false);
      }
      if (
        cityDropdownRef.current &&
        !cityDropdownRef.current.contains(event.target)
      ) {
        setIsCityOpen(false);
      }
      if (
        pincodeDropdownRef.current &&
        !pincodeDropdownRef.current.contains(event.target)
      ) {
        setIsPincodeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const parseNumber = (val) => {
    if (val === "") return null;
    const n = parseFloat(val);
    return isNaN(n) || n < 0 ? null : n;
  };

  const filtered = users.filter((user) => {
    const firstBookingDate = user.bookings?.[0]?.createdAt
      ? new Date(user.bookings[0].createdAt)
      : null;
    const term = searchTerm.toLowerCase();

    const matchesSearch =
      user.fullName?.toLowerCase().includes(term) ||
      user.mobile?.toLowerCase().includes(term);

    const matchesCity = !selectedCity || user.city === selectedCity;
    const matchesPincode = !selectedPincode || user.pincode === selectedPincode;

    const matchesShop =
      !selectedShop ||
      user.bookings?.some((booking) =>
        booking.shopName?.toLowerCase().includes(selectedShop.toLowerCase())
      );

    const matchesDate = (() => {
      if (!firstBookingDate) return !fromDate && !toDate;
      if (fromDate && toDate) {
        const from = new Date(fromDate);
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        return firstBookingDate >= from && firstBookingDate <= to;
      }
      if (fromDate) return firstBookingDate >= new Date(fromDate);
      if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        return firstBookingDate <= to;
      }
      return true;
    })();

    const matchesService =
      !selectedService ||
      user.bookings?.some((booking) =>
        booking.services?.some(
          (s) => String(s.serviceId) === String(selectedService)
        )
      );

    const matchesCategory =
      !selectedCategory ||
      user.bookings?.some((booking) =>
        booking.services?.some(
          (s) =>
            s.categoryName?.toLowerCase() === selectedCategory.toLowerCase()
        )
      );

    const totalAmount = Number(user.totalOrderAmount) || 0;
    const min = parseNumber(minAmount);
    const max = parseNumber(maxAmount);
    const matchesAmount =
      (min === null || totalAmount >= min) &&
      (max === null || totalAmount <= max);

    return (
      matchesSearch &&
      matchesCity &&
      matchesPincode &&
      matchesShop &&
      matchesDate &&
      matchesService &&
      matchesCategory &&
      matchesAmount
    );
  });

  console.log("Filtered users:", filtered.length, filtered);

  const totalPages =
    filtered.length > 0 ? Math.ceil(filtered.length / limit) : 1;
  const currentUsers = filtered.slice((page - 1) * limit, page * limit);

  const toggleDetails = (id) => {
    setShowUserDetails((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDownload = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("User List", 14, 22);

    const columns = [
      "#",
      "Full Name",
      "Mobile",
      "Total Order Amount",
      "Address",
      "City",
      "Pincode",
      "Shop Name",
    ];
    const rows = filtered.map((user, index) => [
      index + 1,
      user.fullName || "N/A",
      user.mobile || "N/A",
      `${(user.totalOrderAmount || 0).toFixed(2)}₹`,
      user.address || "N/A",
      user.city || "N/A",
      user.pincode || "N/A",
      user.bookings?.[0]?.shopName || "N/A",
    ]);

    doc.autoTable({
      startY: 30,
      head: [columns],
      body: rows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 160, 133] },
      theme: "striped",
    });

    doc.save("users-list.pdf");
  };

  const serviceOptions = services.map((s) => ({
    value: s._id,
    label: s.serviceName || "N/A",
  }));

  return (
    <div className="relative flex flex-col bg-white w-full mb-6 shadow-lg rounded-lg">
      <div className="px-4 py-3 border-b border-blueGray-200">
        <h3 className="font-semibold text-base text-blueGray-700 mb-2">
          Customers{" "}
          <span className="ml-2 text-sm text-blueGray-500">
            (Total: {filtered.length})
          </span>
          <button
            onClick={handleDownload}
            className="bg-customBlue text-white px-4 py-2 rounded-md float-right hover:bg-customBlue-hover transition duration-150"
            style={{
              backgroundColor: "#0884c4",
              float: "right",
              width: "100px",
              height: "40px",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              marginTop: "-10px",
            }}
          >
            Download
          </button>
        </h3>

        {/* FILTERS */}
        <div className="bg-gray-50 border rounded-md p-4 mx-4 mb-4 shadow-sm">
          <div className="flex flex-wrap gap-4 mb-4">
            {/* Category Dropdown */}
            <div className="relative w-1/5">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedService("");
                  setPage(1);
                }}
                className="w-full px-3 py-1 border border-blueGray-200 rounded focus:outline-none focus:ring-blueGray max-h-60 overflow-y-auto"
                style={{ height: "38px" }}
              >
                <option value="">All Categories</option>
                {categories.map((category, index) => (
                  <option key={index} value={category.categoryName}>
                    {category.categoryName || "N/A"}
                  </option>
                ))}
              </select>
            </div>

            {/* Shop Dropdown */}
            <div className="relative w-1/5" ref={shopDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Shop Name
              </label>
              <div
                className="w-full px-3 py-1 border border-blueGray-200 rounded cursor-pointer focus:outline-none focus:ring-blueGray"
                style={{ height: "38px" }}
                onClick={() => {
                  setIsShopOpen((prev) => !prev);
                  setIsServiceOpen(false);
                  setIsCityOpen(false);
                  setIsPincodeOpen(false);
                }}
              >
                {selectedShop || "Select Shop"}
              </div>
              {isShopOpen && (
                <div
                  className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg"
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    scrollBehavior: "smooth",
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search shop..."
                    value={searchShop}
                    onChange={(e) => setSearchShop(e.target.value)}
                    className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none"
                  />
                  <ul style={{ scrollBehavior: "smooth" }}>
                    <li
                      className="px-3 py-2 hover:bg-blue-100 cursor-pointer font-semibold"
                      onClick={() => {
                        setSelectedShop("");
                        setIsShopOpen(false);
                        setPage(1);
                      }}
                    >
                      All Shops
                    </li>
                    {filteredShops.length > 0 ? (
                      filteredShops.map((shop) => (
                        <li
                          key={shop.value}
                          className="px-3 py-2 hover:bg-blue-100 cursor-pointer"
                          onClick={() => {
                            setSelectedShop(shop.value);
                            setIsShopOpen(false);
                            setPage(1);
                          }}
                        >
                          {shop.label}
                        </li>
                      ))
                    ) : (
                      <li className="px-3 py-2 text-gray-500">
                        No shops found
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* City Dropdown */}
            <div className="relative w-1/5" ref={cityDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <div
                className="w-full px-3 py-1 border border-blueGray-200 rounded cursor-pointer focus:outline-none focus:ring-blueGray"
                style={{ height: "38px" }}
                onClick={() => {
                  setIsCityOpen((prev) => !prev);
                  setIsServiceOpen(false);
                  setIsShopOpen(false);
                  setIsPincodeOpen(false);
                }}
              >
                {selectedCity || "Select City"}
              </div>
              {isCityOpen && (
                <div
                  className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg"
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    scrollBehavior: "smooth",
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search city..."
                    value={searchCity}
                    onChange={(e) => setSearchCity(e.target.value)}
                    className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none"
                  />
                  <ul style={{ scrollBehavior: "smooth" }}>
                    <li
                      className="px-3 py-2 hover:bg-blue-100 cursor-pointer font-semibold"
                      onClick={() => {
                        setSelectedCity("");
                        setIsCityOpen(false);
                        setPage(1);
                      }}
                    >
                      All Cities
                    </li>
                    {filteredCities.length > 0 ? (
                      filteredCities.map((city) => (
                        <li
                          key={city.value}
                          className="px-3 py-2 hover:bg-blue-100 cursor-pointer"
                          onClick={() => {
                            setSelectedCity(city.value);
                            setIsCityOpen(false);
                            setPage(1);
                          }}
                        >
                          {city.label}
                        </li>
                      ))
                    ) : (
                      <li className="px-3 py-2 text-gray-500">
                        No cities found
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Pincode Dropdown */}
            <div className="relative w-1/5" ref={pincodeDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pincode
              </label>
              <div
                className="w-full px-3 py-1 border border-blueGray-200 rounded cursor-pointer focus:outline-none focus:ring-blueGray"
                style={{ height: "38px" }}
                onClick={() => {
                  setIsPincodeOpen((prev) => !prev);
                  setIsServiceOpen(false);
                  setIsShopOpen(false);
                  setIsCityOpen(false);
                }}
              >
                {selectedPincode || "Select Pincode"}
              </div>
              {isPincodeOpen && (
                <div
                  className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg"
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    scrollBehavior: "smooth",
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search pincode..."
                    value={searchPincode}
                    onChange={(e) => setSearchPincode(e.target.value)}
                    className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none"
                  />
                  <ul style={{ scrollBehavior: "smooth" }}>
                    <li
                      className="px-3 py-2 hover:bg-blue-100 cursor-pointer font-semibold"
                      onClick={() => {
                        setSelectedPincode("");
                        setIsPincodeOpen(false);
                        setPage(1);
                      }}
                    >
                      All Pincodes
                    </li>
                    {filteredPincodes.length > 0 ? (
                      filteredPincodes.map((pincode) => (
                        <li
                          key={pincode.value}
                          className="px-3 py-2 hover:bg-blue-100 cursor-pointer"
                          onClick={() => {
                            setSelectedPincode(pincode.value);
                            setIsPincodeOpen(false);
                            setPage(1);
                          }}
                        >
                          {pincode.label}
                        </li>
                      ))
                    ) : (
                      <li className="px-3 py-2 text-gray-500">
                        No pincodes found
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
                className="w-40 border rounded-md px-2 py-1 text-sm"
                style={{ height: "38px" }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
                className="w-40 border rounded-md px-2 py-1 text-sm"
                style={{ height: "38px" }}
              />
            </div>

            {/* Amount Range */}
            <div className="flex flex-col sm:flex-row gap-2 items-center">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Min Amount
                </label>
                <input
                  type="number"
                  value={minAmount}
                  onChange={(e) => {
                    setMinAmount(e.target.value);
                    setPage(1);
                  }}
                  className="w-40 border rounded-md px-2 py-1 text-sm"
                  placeholder="Min ₹"
                  min="0"
                  style={{ height: "38px" }}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Max Amount
                </label>
                <input
                  type="number"
                  value={maxAmount}
                  onChange={(e) => {
                    setMaxAmount(e.target.value);
                    setPage(1);
                  }}
                  className="w-40 border rounded-md px-2 py-1 text-sm"
                  placeholder="Max ₹"
                  min="0"
                  style={{ height: "38px" }}
                />
              </div>
            </div>
          </div>

          <input
            type="text"
            placeholder="Search by name or mobile…"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="w-full md:w-1/3 px-3 py-2 border border-blueGray-200 rounded focus:outline-none"
            style={{ height: "38px" }}
          />
        </div>
      </div>

      {/* USER TABLE */}
      <div className="overflow-x-auto">
        <table className="w-full bg-transparent border-collapse">
          <thead>
            <tr>
              {[
                "#",
                "Full Name",
                "Mobile",
                "Total Order Amount",
                "View Details",
              ].map((h, i) => (
                <th
                  key={i}
                  className="px-6 py-3 bg-blueGray-50 text-blueGray-500 text-xs uppercase font-semibold text-left border border-blueGray-100"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentUsers.length > 0 ? (
              currentUsers.map((user, idx) => {
                const id = user._id;
                return (
                  <React.Fragment key={id}>
                    <tr>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {(page - 1) * limit + idx + 1}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {user.fullName || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {user.mobile || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        ₹{(user.totalOrderAmount || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        <button
                          onClick={() => toggleDetails(id)}
                          className="bg-lightBlue-500 text-white px-3 py-1 rounded text-xs font-bold hover:bg-lightBlue-600 transition"
                        >
                          {showUserDetails[id]
                            ? "Hide Details"
                            : "View Details"}
                        </button>
                      </td>
                    </tr>
                    {showUserDetails[id] && (
                      <tr>
                        <td
                          colSpan="5"
                          className="px-6 py-4 border-t border-blueGray-100"
                        >
                          <div className="bg-white shadow rounded p-4">
                            <h4 className="font-semibold text-lg mb-2">
                              {user.fullName}
                            </h4>
                            <p>
                              <strong>Mobile:</strong> {user.mobile || "N/A"}
                            </p>
                            <p>
                              <strong>Shop:</strong>{" "}
                              {user.bookings?.[0]?.shopName || "N/A"}
                            </p>
                            <p>
                              <strong>Address:</strong> {user.address || "N/A"}
                            </p>
                            <p>
                              <strong>City/Village:</strong>{" "}
                              {user.city || "N/A"}
                            </p>
                            <p>
                              <strong>Category:</strong>{" "}
                              {user.bookings?.length > 0
                                ? [
                                  ...new Set(
                                    user.bookings
                                      .flatMap((booking) =>
                                        booking.services?.map(
                                          (service) => service.categoryName
                                        )
                                      )
                                      .filter((cat) => !!cat)
                                  ),
                                ].join(", ") || "N/A"
                                : "N/A"}
                            </p>
                            <p>
                              <strong>Pincode:</strong> {user.pincode || "N/A"}
                            </p>
                            <p>
                              <strong>Total Order Amount:</strong> ₹
                              {(user.totalOrderAmount || 0).toFixed(2)}
                            </p>
                            {user.bookings?.length ? (
                              <div className="mt-4">
                                <h5 className="font-semibold mb-1">
                                  Bookings:
                                </h5>
                                {user.bookings.map((booking, i) => (
                                  <div key={i} className="mb-2">
                                    <p className="italic text-sm">
                                      Booking {i + 1} (Status:{" "}
                                      {booking.status || "N/A"}, Created:{" "}
                                      {new Date(
                                        booking.createdAt
                                      ).toLocaleString()}
                                      )
                                    </p>
                                    <ul className="list-disc list-inside text-sm ml-4 mt-1">
                                      {booking.services?.map((s, j) => (
                                        <li key={j}>
                                          {s.serviceName || "Unnamed Service"} –
                                          ₹{(s.price || 0).toFixed(2)} (Qty:{" "}
                                          {s.quantity || 1}) = ₹
                                          {(s.subtotal || 0).toFixed(2)}
                                          {s.categoryName
                                            ? ` (Category: ${s.categoryName})`
                                            : ""}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm mt-2">No bookings found</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan="5"
                  className="p-4 text-center text-sm text-blueGray-500"
                >
                  No users found. Check the console for errors.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > limit && (
        <div className="flex justify-between items-center px-4 py-3">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-blueGray-200 rounded hover:bg-blueGray-300 transition disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-blueGray-700">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-blueGray-200 rounded hover:bg-blueGray-300 transition disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default GetUser;